import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { getBreadcrumbBackPath } from "@/lib/breadcrumbs";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import SectionCard from "@/pages/appointmentDetails/SectionCard";
import { fmtDateTime } from "@/pages/appointmentDetails/helpers";
import useCommissionCatalog from "@/hooks/useCommissionCatalog";

const INSURANCE_TYPES = ["Annuity", "Term Life", "Universal Life", "Whole Life"];
const ENROLLMENT_TYPES = ["Underwritten", "Guaranteed Issue"];
const COVERAGE_LENGTH_OPTIONS = ["N/A", "10", "15", "20", "25", "30", "35", "40"];
const SPLIT_PERCENT_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const PRODUCT_STATUS_OPTIONS = ["Active", "Pending", "Decline", "Cancelled", "Withdrawn"];
const PRODUCT_SUB_STATUS_OPTIONS = [
  "Inforce",
  "Pending Payment",
  "Initial Payment Decline",
  "Monthly Payment Decline",
  "Telephone Interview Needed",
  "Resubmit Needed",
  "Amendment Needed",
  "Dead Deal",
  "Unknown",
  "Pending Underwriting",
];

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "—";
  return String(value);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(num) ? null : num;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const normalizeMatch = (value, options) => {
  if (!value || !Array.isArray(options) || options.length === 0) return value || "";
  const direct = options.find((option) => option === value);
  if (direct) return direct;
  const normalized = String(value).toLowerCase().trim();
  const match = options.find((option) => String(option).toLowerCase().trim() === normalized);
  return match || value;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const toDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

export default function ProductDetailsPage() {
  const { clientId, productIndex } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();

  // Function to invalidate clients list cache after mutations
  const invalidateClientsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.clients.all(selectedTenantId),
    });
    // Also invalidate the specific client detail
    queryClient.invalidateQueries({
      queryKey: queryKeys.clients.detail(selectedTenantId, clientId),
    });
  }, [queryClient, selectedTenantId, clientId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [salesAgents, setSalesAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [commissionProfiles, setCommissionProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);

  const {
    companyOptions,
    ensureProducts,
    getCompanyIdByName,
    getProductOptionsByCompanyName,
  } = useCommissionCatalog();

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/clients/${clientId}`);
        setClient(res.data?.client || null);
        setAppointment(res.data?.latest_appointment || null);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load product details.");
        toast({ title: "Could not load product", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [clientId, toast]);

  useEffect(() => {
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        const res = await api.get("/admin/users");
        const list = Array.isArray(res.data) ? res.data : [];
        setSalesAgents(
          list.filter(
            (u) =>
              ["agent", "booking_agent"].includes(u.role) ||
              (["admin", "super_admin"].includes(u.role) && u.list_as_sales_agent)
          )
        );
      } catch {
        setSalesAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };
    loadAgents();
  }, []);

  useEffect(() => {
    const loadProfiles = async () => {
      setProfilesLoading(true);
      try {
        const res = await api.get("/commission/profiles");
        setCommissionProfiles(Array.isArray(res.data) ? res.data : []);
      } catch {
        setCommissionProfiles([]);
      } finally {
        setProfilesLoading(false);
      }
    };
    loadProfiles();
  }, [clientId]);

  useEffect(() => {
    if (!editOpen) return;
    const companyName = editValues.company?.name || editValues.company || "";
    if (!companyName) return;
    const companyId = getCompanyIdByName(companyName);
    if (companyId) ensureProducts(companyId);
  }, [editOpen, editValues.company, getCompanyIdByName, ensureProducts]);

  const productRecords = useMemo(() => {
    if (client?.sold_payloads?.length) return client.sold_payloads;
    if (client?.sold_payload) return [client.sold_payload];
    if (Array.isArray(appointment?.sold_payloads) && appointment.sold_payloads.length) return appointment.sold_payloads;
    if (appointment?.sold_payload) return [appointment.sold_payload];
    return [];
  }, [client, appointment]);

  const index = Number(productIndex || 0);
  const product = productRecords[index] || productRecords[0] || null;

  const pageTitle = product?.product_name || "Product Details";
  usePageTitle(pageTitle);

  const editIconButtonClass =
    "h-9 w-9 rounded-full bg-indigo-500/20 p-0 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30";

  const handleRecalculate = async () => {
    if (!clientId || recalcLoading) return;
    setRecalcLoading(true);
    try {
      const res = await api.post(`/clients/${clientId}/recalculate-commissions`, null, {
        params: { product_index: index || 0 },
      });
      setClient(res.data || null);
      toast({ title: "Commissions recalculated" });
    } catch (e) {
      toast({
        title: "Could not recalculate commissions",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRecalcLoading(false);
    }
  };

  const policyFields = useMemo(() => {
    const insuranceType = String(product?.insurance_type || "").toLowerCase();
    const isWholeLife = insuranceType === "whole life";
    const isTermLife = insuranceType === "term life";
    const isUniversal = insuranceType === "universal life";
    const isAnnuity = insuranceType === "annuity";

    const monthlyPremiumRaw = parseNumber(product?.monthly_premium);
    const targetPremiumRaw = parseNumber(product?.annual_target_premium);
    const annualPremiumRaw = parseNumber(product?.annual_premium);
    const annualPremiumValue = monthlyPremiumRaw !== null ? monthlyPremiumRaw * 12 : annualPremiumRaw;

    const fields = [
      { key: "product_status", label: "Product Status", value: product?.product_status || product?.status },
      { key: "product_sub_status", label: "Product Sub-Status", value: product?.product_sub_status || product?.sub_status },
      { key: "enrollment_type", label: "Enrollment Type", value: product?.enrollment_type },
      { key: "product_type", label: "Product Type", value: product?.insurance_type },
      { key: "carrier", label: "Carrier", value: product?.company },
      { key: "product_name", label: "Product Name", value: product?.product_name },
      { key: "policy_number", label: "Policy Number", value: product?.policy_number },
    ];

    if (targetPremiumRaw !== null) {
      fields.push({
        key: "annual_target_premium",
        label: isWholeLife ? "Target Premium" : "Annual Target Premium",
        value: formatCurrency(targetPremiumRaw),
      });
    }

    if (annualPremiumValue !== null && (isTermLife || isWholeLife || isAnnuity)) {
      fields.push({
        key: "annual_premium",
        label: "Annual Premium",
        value: formatCurrency(annualPremiumValue),
      });
    }

    if (monthlyPremiumRaw !== null) {
      fields.push({
        key: "monthly_premium",
        label: "Monthly Premium",
        value: formatCurrency(monthlyPremiumRaw),
      });
    }

    fields.push({ key: "coverage_amount", label: "Coverage Amount", value: formatCurrency(product?.death_benefit) });
    fields.push({
      key: "application_date",
      label: "Application Create Date",
      value: fmtDateTime(product?.application_date || appointment?.start_at || client?.created_at),
    });

    return fields;
  }, [appointment, product, client]);

  const resolvedCommissionProfile = useMemo(() => {
    if (!commissionProfiles.length || !product) return null;

    const companyName =
      product?.company?.name || product?.company || product?.insurance_company?.name || product?.insurance_company || "";
    const productName = product?.product_name?.name || product?.product_name || product?.product || "";
    if (!companyName || !productName) return null;

    const issueAge = product?.issue_age !== undefined && product?.issue_age !== null ? String(product?.issue_age).trim() : "";
    const applicationDate = toDateOnly(product?.application_date || appointment?.start_at || client?.created_at);

    const matches = commissionProfiles.filter((profile) => {
      const profileCompany = profile.company_name || profile.company || "";
      const profileProduct = profile.product_name || "";
      if (normalizeText(profileCompany) !== normalizeText(companyName)) return false;
      if (normalizeText(profileProduct) !== normalizeText(productName)) return false;

      const profileIssueAge = profile.issue_age !== undefined && profile.issue_age !== null ? String(profile.issue_age).trim() : "";
      if (issueAge && profileIssueAge && profileIssueAge !== issueAge) return false;
      if (issueAge && !profileIssueAge) return false;
      return true;
    });

    if (!matches.length) return null;

    const isActive = (profile) => {
      if (!applicationDate) return true;
      const start = toDateOnly(profile?.effective_start_date);
      if (start && start.getTime() > applicationDate.getTime()) return false;
      const end = toDateOnly(profile?.effective_end_date);
      if (end && end.getTime() < applicationDate.getTime()) return false;
      return true;
    };

    const activeMatches = matches.filter((profile) => isActive(profile));
    const selected = activeMatches[0] || matches[0];
    if (!selected) return null;

    const agentId =
      product?.sales_agent_id || appointment?.sales_agent_user_id || appointment?.created_by_user_id || null;
    const overrides = Array.isArray(selected.agent_overrides) ? selected.agent_overrides : [];
    const override = agentId ? overrides.find((row) => row?.user_id === agentId) : null;

    return {
      agent_commission_percent: override?.agent_commission_percent ?? selected.default_agent_commission_percent,
      agent_adv_months: override?.agent_advance_months ?? selected.default_agent_advance_months,
      house_commission_percent: selected.house_commission_percent,
      house_adv_months: selected.house_advance_months,
    };
  }, [commissionProfiles, product, appointment, client]);

  const commissionFields = useMemo(
    () => {
      const monthlyPremiumValue = parseNumber(product?.monthly_premium);
      const targetPremiumRaw = parseNumber(product?.annual_target_premium);
      const annualTargetValue =
        targetPremiumRaw ?? parseNumber(product?.annual_premium) ?? (monthlyPremiumValue ? monthlyPremiumValue * 12 : null);
      const resolvedMonthlyPremium = monthlyPremiumValue ?? (annualTargetValue ? annualTargetValue / 12 : null);
      const insuranceType = String(product?.insurance_type || "").toLowerCase();
      const isWholeLife = insuranceType === "whole life";
      const isTermLife = insuranceType === "term life";
      const isUniversal = insuranceType === "universal life";
      const isAnnuity = insuranceType === "annuity";
      const annualPremiumValue = monthlyPremiumValue !== null ? monthlyPremiumValue * 12 : parseNumber(product?.annual_premium);
      const splitCaseValue =
        product?.split_case === undefined || product?.split_case === null || product?.split_case === ""
          ? null
          : product?.split_case;
      const splitPercentValue = parseNumber(product?.split_percent);
      const agentCommissionPercentRaw = parseNumber(product?.agent_commission_percent);
      const houseCommissionPercentRaw = parseNumber(product?.house_commission_percent);
      const agentAdvMonthsRaw = parseNumber(product?.agent_adv_months);
      const houseAdvMonthsRaw = parseNumber(product?.house_adv_months);
      const resolvedAgentCommissionPercent = parseNumber(resolvedCommissionProfile?.agent_commission_percent);
      const resolvedHouseCommissionPercent = parseNumber(resolvedCommissionProfile?.house_commission_percent);
      const resolvedAgentAdvMonths = parseNumber(resolvedCommissionProfile?.agent_adv_months);
      const resolvedHouseAdvMonths = parseNumber(resolvedCommissionProfile?.house_adv_months);
      const agentCommissionPercent = resolvedAgentCommissionPercent ?? agentCommissionPercentRaw;
      const houseCommissionPercent = resolvedHouseCommissionPercent ?? houseCommissionPercentRaw;
      const agentAdvMonths = resolvedAgentAdvMonths ?? agentAdvMonthsRaw;
      const houseAdvMonths = resolvedHouseAdvMonths ?? houseAdvMonthsRaw;
      const agentCommissionRaw = parseNumber(product?.agent_commission);
      const houseCommissionRaw = parseNumber(product?.house_commission);

      const splitCaseFlag = splitCaseValue === true || String(splitCaseValue || "").toLowerCase() === "yes";
      const effectiveSplitPercent = splitCaseFlag ? splitPercentValue ?? 100 : 100;

      let basePremium = resolvedMonthlyPremium;
      if (insuranceType === "annuity") {
        basePremium = parseNumber(product?.annual_premium);
      } else if (insuranceType === "universal life") {
        basePremium = annualTargetValue ? annualTargetValue / 12 : resolvedMonthlyPremium;
      }

      const computedAgentCommission =
        agentCommissionPercent !== null && agentAdvMonths !== null && basePremium !== null
          ? basePremium * (agentCommissionPercent / 100) * agentAdvMonths * (effectiveSplitPercent / 100)
          : null;
      const computedHouseCommission =
        houseCommissionPercent !== null && houseAdvMonths !== null && basePremium !== null
          ? basePremium * (houseCommissionPercent / 100) * houseAdvMonths * (effectiveSplitPercent / 100)
          : null;

      const agentCommission =
        agentCommissionRaw && agentCommissionRaw !== 0
          ? agentCommissionRaw
          : computedAgentCommission !== null && computedAgentCommission !== 0
            ? Number(computedAgentCommission.toFixed(2))
            : agentCommissionRaw;
      const houseCommission =
        houseCommissionRaw && houseCommissionRaw !== 0
          ? houseCommissionRaw
          : computedHouseCommission !== null && computedHouseCommission !== 0
            ? Number(computedHouseCommission.toFixed(2))
            : houseCommissionRaw;
      const secondAgentCommission = parseNumber(product?.second_agent_commission);

      const fields = [];

      if (targetPremiumRaw !== null) {
        fields.push({
          key: "annual_target_premium",
          label: isWholeLife ? "Target Premium" : "Annual Target Premium",
          value: targetPremiumRaw,
          format: "currency",
        });
      }

      if (annualPremiumValue !== null && (isTermLife || isWholeLife || isAnnuity)) {
        fields.push({ key: "annual_premium", label: "Annual Premium", value: annualPremiumValue, format: "currency" });
      }

      if (monthlyPremiumValue !== null) {
        fields.push({ key: "monthly_premium", label: "Monthly Premium", value: monthlyPremiumValue, format: "currency" });
      }

      fields.push({ key: "split_case", label: "Was this a Split Case?", value: splitCaseValue, format: "text" });
      fields.push({ key: "sales_agent_commission", label: "Sales Agent Commission", value: agentCommission, format: "currency" });
      fields.push({ key: "house_commission", label: "House Commission", value: houseCommission, format: "currency" });
      fields.push({
        key: "sales_agent_commission_percent",
        label: "Sales Agent Commission %",
        value: agentCommissionPercent,
        format: "text",
      });
      fields.push({
        key: "house_commission_percent",
        label: "House Commission %",
        value: houseCommissionPercent,
        format: "text",
      });
      fields.push({ key: "second_agent_commission", label: "Second Agent Commission", value: secondAgentCommission, format: "currency" });
      fields.push({ key: "second_agent_split", label: "Second Agent Split %", value: splitPercentValue, format: "text" });
      fields.push({ key: "issue_age", label: "Issue Age", value: product?.issue_age, format: "text" });
      fields.push({ key: "sales_agent_adv_months", label: "Sales Agent Adv Months", value: agentAdvMonths, format: "text" });
      fields.push({ key: "house_adv_months", label: "House Adv Months", value: houseAdvMonths, format: "text" });

      return fields.filter((field) => field.value !== null && field.value !== undefined && field.value !== "");
    },
    [product, resolvedCommissionProfile]
  );

  const agentFields = useMemo(
    () =>
      [
        { key: "presentation_agent", label: "Sales Agent", value: product?.sales_agent || appointment?.sales_agent_name },
        { key: "second_agent", label: "Second Agent", value: product?.split_agent },
      ].filter((field) => field.key !== "second_agent" || field.value),
    [product, appointment]
  );

  const premiumFieldKey = editValues.insurance_type === "Universal Life" ? "annual_target_premium" : "annual_premium";
  const premiumFieldLabel =
    editValues.insurance_type === "Universal Life" ? "Annual Target Premium" : "Annual Premium";

  const companyList = useMemo(() => {
    const profileCompanies = commissionProfiles
      .map((p) => p.company_name || p.company)
      .filter(Boolean);
    return Array.from(new Set([...(companyOptions || []), ...profileCompanies]));
  }, [companyOptions, commissionProfiles]);

  const productOptions = useMemo(() => {
    const companyName = editValues.company?.name || editValues.company || "";
    const normalizedCompany = normalizeMatch(companyName, companyList);
    const catalogOptions = normalizedCompany ? getProductOptionsByCompanyName(normalizedCompany) || [] : [];
    const profileOptions = commissionProfiles
      .filter(
        (p) =>
          String(p.company_name || p.company || "").toLowerCase().trim() ===
          String(normalizedCompany || "").toLowerCase().trim()
      )
      .map((p) => p.product_name)
      .filter(Boolean);
    const current = editValues.product_name ? [editValues.product_name] : [];
    return Array.from(
      new Set([...catalogOptions, ...profileOptions, ...current].filter(Boolean))
    );
  }, [editValues.company, editValues.product_name, getProductOptionsByCompanyName, commissionProfiles, companyList]);

  const issueAgeOptions = useMemo(() => {
    const companyName = editValues.company?.name || editValues.company || "";
    const productName = editValues.product_name?.name || editValues.product_name || "";
    const normalizedCompany = normalizeMatch(companyName, companyList);
    const normalizedProduct = normalizeMatch(productName, productOptions);
    const profileOptions = commissionProfiles
      .filter((p) => {
        const companyMatch =
          String(p.company_name || p.company || "").toLowerCase().trim() ===
          String(normalizedCompany || "").toLowerCase().trim();
        const productMatch =
          String(p.product_name || "").toLowerCase().trim() ===
          String(normalizedProduct || "").toLowerCase().trim();
        return companyMatch && productMatch;
      })
      .map((p) => p.issue_age)
      .filter(Boolean);
    const defaults = profileOptions.length ? profileOptions : ["0-70", "18-80", "0-65", "0-100"];
    const current = editValues.issue_age ? [editValues.issue_age] : [];
    return Array.from(new Set([...current, ...defaults])).filter(Boolean);
  }, [editValues.issue_age, editValues.company, editValues.product_name, commissionProfiles, companyList, productOptions]);

  useEffect(() => {
    if (!editOpen || !editValues.company || companyList.length === 0) return;
    const matched = normalizeMatch(editValues.company, companyList);
    if (matched && matched !== editValues.company) {
      setEditValues((prev) => ({ ...(prev || {}), company: matched }));
    }
  }, [editOpen, editValues.company, companyList]);

  useEffect(() => {
    if (!editOpen || !editValues.product_name || productOptions.length === 0) return;
    const matched = normalizeMatch(editValues.product_name, productOptions);
    if (matched && matched !== editValues.product_name) {
      setEditValues((prev) => ({ ...(prev || {}), product_name: matched }));
    }
  }, [editOpen, editValues.product_name, productOptions]);

  const openEdit = () => {
    const rawSplitCase = product?.split_case;
    const splitCaseValue =
      rawSplitCase === true ? "Yes" : rawSplitCase === false ? "No" : rawSplitCase || "";
    setEditValues({
      sales_agent: product?.sales_agent || appointment?.sales_agent_name || "",
      application_date: toDateInputValue(product?.application_date || appointment?.start_at || client?.created_at),
      insurance_type: product?.insurance_type || "",
      enrollment_type: product?.enrollment_type || "",
      company: product?.company?.name || product?.company || product?.insurance_company?.name || product?.insurance_company || "",
      product_name: product?.product_name?.name || product?.product_name || product?.product || "",
      coverage_length: product?.coverage_length || "",
      issue_age: product?.issue_age || "",
      annual_premium: product?.annual_premium || "",
      annual_target_premium: product?.annual_target_premium || "",
      split_case: splitCaseValue,
      split_percent: product?.split_percent || "",
      split_agent: product?.split_agent || "",
      policy_number: product?.policy_number || "",
      product_status: product?.product_status || product?.policy_status || "",
      product_sub_status: product?.product_sub_status || product?.sub_status || "",
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!clientId) return;
    setEditSaving(true);
    try {
      const payload = {
        ...editValues,
        split_case:
          editValues.split_case === "Yes"
            ? true
            : editValues.split_case === "No"
              ? false
              : editValues.split_case,
      };
      const res = await api.put(`/clients/${clientId}/products/${index}`, { sold_payload: payload });
      setClient(res.data || null);
      setEditOpen(false);
      toast({ title: "Product updated" });
      
      // REACT QUERY: Invalidate clients cache to reflect status/sub_status changes in list
      invalidateClientsCache();
    } catch (e) {
      toast({ title: "Could not update product", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="product-details-page">
      <div className="flex items-center justify-between gap-3" data-testid="product-details-toolbar">
        <Button
          type="button"
          variant="secondary"
          className="h-9 rounded-full bg-indigo-500/15 px-4 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/25"
          onClick={() => navigate(getBreadcrumbBackPath(`/app/clients/${clientId}`))}
          data-testid="product-details-back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>

      {loading ? (
        <Card className="glass" data-testid="product-details-loading">
          <CardContent className="p-6 text-sm text-slate-200/70">Loading product details…</CardContent>
        </Card>
      ) : error ? (
        <Card className="glass" data-testid="product-details-error">
          <CardContent className="p-6 text-sm text-rose-200">{error}</CardContent>
        </Card>
      ) : (
        <>
          <SectionCard
            title="Product Details"
            right={
              <Button
                type="button"
                variant="secondary"
                className={editIconButtonClass}
                onClick={openEdit}
                data-testid="product-details-edit-button"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-details-policy">
              {policyFields.map((field) => (
                <div key={field.key} className="space-y-1" data-testid={`product-details-policy-${field.key}`}>
                  <div className="text-xs text-slate-200/60" data-testid={`product-details-policy-label-${field.key}`}>
                    {field.label}
                  </div>
                  <div className="text-sm text-slate-100" data-testid={`product-details-policy-value-${field.key}`}>
                    {formatValue(field.value)}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Commission Details"
            right={
              <Button
                type="button"
                variant="secondary"
                className="h-9 rounded-full bg-white/10 px-4 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                onClick={handleRecalculate}
                disabled={recalcLoading}
                data-testid="product-details-recalculate-commissions"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${recalcLoading ? "animate-spin" : ""}`} />
                {recalcLoading ? "Recalculating" : "Recalculate"}
              </Button>
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-details-commission">
              {commissionFields.map((field) => (
                <div key={field.key} className="space-y-1" data-testid={`product-details-commission-${field.key}`}>
                  <div className="text-xs text-slate-200/60" data-testid={`product-details-commission-label-${field.key}`}>
                    {field.label}
                  </div>
                  <div className="text-sm text-slate-100" data-testid={`product-details-commission-value-${field.key}`}>
                    {field.value === null || field.value === undefined || field.value === "" ? (
                      <span className="text-amber-300">Missing</span>
                    ) : field.format === "currency" ? (
                      formatCurrency(field.value)
                    ) : (
                      formatValue(field.value)
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Agent Details">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="product-details-agent">
              {agentFields.map((field) => (
                <div key={field.key} className="space-y-1" data-testid={`product-details-agent-${field.key}`}>
                  <div className="text-xs text-slate-200/60" data-testid={`product-details-agent-label-${field.key}`}>
                    {field.label}
                  </div>
                  <div className="text-sm text-slate-100" data-testid={`product-details-agent-value-${field.key}`}>
                    {formatValue(field.value)}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit</DialogTitle>
          </DialogHeader>

          <div className="space-y-5" data-testid="product-details-edit-fields">
            <div className="space-y-2" data-testid="product-details-edit-sales-agent">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Sales Agent</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Select
                value={editValues.sales_agent || ""}
                onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), sales_agent: value }))}
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-sales-agent-trigger">
                  <SelectValue placeholder={agentsLoading ? "Loading..." : "Select"} />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-sales-agent-content">
                  {salesAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.name} data-testid={`product-details-edit-sales-agent-${agent.id}`}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-application-date">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Application Create Date</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Input
                type="date"
                value={editValues.application_date || ""}
                onChange={(e) => setEditValues((prev) => ({ ...(prev || {}), application_date: e.target.value }))}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="product-details-edit-application-date-input"
              />
            </div>

            <div className="space-y-2" data-testid="product-details-edit-product-type">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Product Type</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {INSURANCE_TYPES.map((option) => {
                  const isActive = editValues.insurance_type === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`rounded-full px-4 py-2 text-sm ring-1 transition ${
                        isActive
                          ? "bg-indigo-500/30 text-indigo-100 ring-indigo-400/40"
                          : "bg-white/5 text-slate-100/80 ring-white/10 hover:bg-white/10"
                      }`}
                      onClick={() => setEditValues((prev) => ({ ...(prev || {}), insurance_type: option }))}
                      data-testid={`product-details-edit-product-type-${option.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-enrollment-type">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Enrollment Type</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {ENROLLMENT_TYPES.map((option) => {
                  const isActive = editValues.enrollment_type === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`rounded-full px-4 py-2 text-sm ring-1 transition ${
                        isActive
                          ? "bg-indigo-500/30 text-indigo-100 ring-indigo-400/40"
                          : "bg-white/5 text-slate-100/80 ring-white/10 hover:bg-white/10"
                      }`}
                      onClick={() => setEditValues((prev) => ({ ...(prev || {}), enrollment_type: option }))}
                      data-testid={`product-details-edit-enrollment-type-${option.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-company">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Insurance Company</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Select
                value={editValues.company?.name || editValues.company || ""}
                onValueChange={(value) =>
                  setEditValues((prev) => ({
                    ...(prev || {}),
                    company: value,
                    product_name:
                      prev?.product_name && !getProductOptionsByCompanyName(value)?.includes(prev.product_name)
                        ? ""
                        : prev?.product_name,
                  }))
                }
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-company-trigger">
                  <SelectValue placeholder={profilesLoading ? "Loading..." : "Select"} />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-company-content">
                  {companyList.map((company) => (
                    <SelectItem key={company} value={company} data-testid={`product-details-edit-company-${company}`}>
                      {company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-product-name">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Product Name</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Select
                value={editValues.product_name?.name || editValues.product_name || ""}
                onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), product_name: value }))}
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-product-name-trigger">
                  <SelectValue placeholder={editValues.company ? "Select" : "Choose company"} />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-product-name-content">
                  {productOptions.map((name) => (
                    <SelectItem key={name} value={name} data-testid={`product-details-edit-product-name-${name}`}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-coverage-length">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Coverage Length</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Select
                value={editValues.coverage_length || ""}
                onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), coverage_length: value }))}
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-coverage-length-trigger">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-coverage-length-content">
                  {COVERAGE_LENGTH_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option} data-testid={`product-details-edit-coverage-length-${option}`}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-premium">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">{premiumFieldLabel}</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Input
                value={editValues[premiumFieldKey] || ""}
                onChange={(e) =>
                  setEditValues((prev) => ({
                    ...(prev || {}),
                    [premiumFieldKey]: e.target.value,
                  }))
                }
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="product-details-edit-premium-input"
              />
            </div>

            <div className="space-y-2" data-testid="product-details-edit-issue-age">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Issue Age</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <Select
                value={editValues.issue_age || ""}
                onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), issue_age: value }))}
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-issue-age-trigger">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-issue-age-content">
                  {issueAgeOptions.map((option) => (
                    <SelectItem key={option} value={option} data-testid={`product-details-edit-issue-age-${option}`}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-split-case">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200/80">Was this a Split Case?</Label>
                <span className="text-xs text-slate-200/40">Required</span>
              </div>
              <div className="flex gap-2">
                {["Yes", "No"].map((option) => {
                  const isActive = editValues.split_case === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`rounded-full px-4 py-2 text-sm ring-1 transition ${
                        isActive
                          ? "bg-indigo-500/30 text-indigo-100 ring-indigo-400/40"
                          : "bg-white/5 text-slate-100/80 ring-white/10 hover:bg-white/10"
                      }`}
                      onClick={() => setEditValues((prev) => ({ ...(prev || {}), split_case: option }))}
                      data-testid={`product-details-edit-split-case-${option.toLowerCase()}`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            {editValues.split_case === "Yes" ? (
              <div className="space-y-2" data-testid="product-details-edit-split-percent">
                <Label className="text-slate-200/80">
                  {`${editValues.sales_agent || "Sales Agent"} Commission Split %`}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {SPLIT_PERCENT_OPTIONS.map((option) => {
                    const isActive = String(editValues.split_percent) === String(option);
                    return (
                      <button
                        key={option}
                        type="button"
                        className={`rounded-full px-4 py-2 text-sm ring-1 transition ${
                          isActive
                            ? "bg-indigo-500/30 text-indigo-100 ring-indigo-400/40"
                            : "bg-white/5 text-slate-100/80 ring-white/10 hover:bg-white/10"
                        }`}
                        onClick={() => setEditValues((prev) => ({ ...(prev || {}), split_percent: option }))}
                        data-testid={`product-details-edit-split-percent-${option}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {editValues.split_case === "Yes" ? (
              <div className="space-y-2" data-testid="product-details-edit-second-agent">
                <Label className="text-slate-200/80">Second Agent</Label>
                <Select
                  value={editValues.split_agent || ""}
                  onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), split_agent: value }))}
                >
                  <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-second-agent-trigger">
                    <SelectValue placeholder={agentsLoading ? "Loading..." : "Select"} />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10" data-testid="product-details-edit-second-agent-content">
                    {salesAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.name} data-testid={`product-details-edit-second-agent-${agent.id}`}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-2" data-testid="product-details-edit-policy-number">
              <Label className="text-slate-200/80">Policy Number</Label>
              <Input
                value={editValues.policy_number || ""}
                onChange={(e) => setEditValues((prev) => ({ ...(prev || {}), policy_number: e.target.value }))}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="product-details-edit-policy-number-input"
              />
            </div>

            <div className="space-y-2" data-testid="product-details-edit-product-status">
              <Label className="text-slate-200/80">Product Status</Label>
              <Select
                value={editValues.product_status || ""}
                onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), product_status: value }))}
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-product-status-trigger">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-product-status-content">
                  {PRODUCT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option} data-testid={`product-details-edit-product-status-${option}`}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="product-details-edit-product-sub-status">
              <Label className="text-slate-200/80">Product Sub Status</Label>
              <Select
                value={editValues.product_sub_status || ""}
                onValueChange={(value) => setEditValues((prev) => ({ ...(prev || {}), product_sub_status: value }))}
              >
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="product-details-edit-product-sub-status-trigger">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10" data-testid="product-details-edit-product-sub-status-content">
                  {PRODUCT_SUB_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option} data-testid={`product-details-edit-product-sub-status-${option}`}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              className="rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20 hover:bg-white/20"
              onClick={() => setEditOpen(false)}
              disabled={editSaving}
              data-testid="product-details-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
              onClick={handleSave}
              disabled={editSaving}
              data-testid="product-details-edit-save"
            >
              {editSaving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}